import { POSButton } from '@publicisgroupe/p-os-components';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as yup from 'yup';
import { Field, XdmContextInfo } from '../../interfaces/Field';
import {
  FieldDependencies,
  calculateFieldsDependencies,
  getFieldsXdmDataFilters,
} from '../../utils/xdmContextUtils';
import { XdmMultiselectDropdown } from '../XdmMultiselectDropdown';
import './XdmContextItem.less';
import { range } from '../../utils/arrayUtils';
import { XdmSelectDropdown } from '../XdmSelectDropdown/XdmSelectDropdown';
import { Entity } from '../../interfaces/Entity';
import useGetXdmEntitiesDataQuery from '../../hooks/queries/useGetXdmEntitiesDataQuery';
import { Label } from 'semantic-ui-react';
import { clsx } from 'clsx';
import { requiredFieldMessage } from '../../constants/errorMessages';
import { ValidationError } from 'yup';

export interface XdmContextItemProps {
  field: Field;
  initialValues: any;
  showValidationErrors?: boolean;
  header?: string;
  isFilterItem?: boolean;
  onChange?: (newValue: any) => void;
  onSubmit?: (newValue: any) => void;
  onClose?: () => void;
}

export const XdmContextItem: React.FC<XdmContextItemProps> = ({
  field,
  initialValues,
  header,
  showValidationErrors,
  isFilterItem,
  onChange,
  onSubmit,
  onClose,
}) => {
  const itemsPerRow = 3;
  const [errors, setErrors] = useState<{ [key: string]: string } | null>(null);
  const [showErrors, setShowErrors] = useState(showValidationErrors ?? false);
  const [currentValues, setCurrentValues] = useState(initialValues);

  const xdmContextInfo = useMemo(() => {
    return field.xdmContext as XdmContextInfo;
  }, [field]);

  const fieldsDependencies = useMemo(() => {
    return calculateFieldsDependencies(xdmContextInfo).sort((a, b) =>
      a.up.length > b.up.length ? 1 : -1
    );
  }, [xdmContextInfo]);

  const { data: xdmEntitiesData, isLoading: xdmEntitiesDataLoading } =
    useGetXdmEntitiesDataQuery(
      xdmContextInfo.mappingTables
        .map((m) => m.entityName)
        .concat(
          xdmContextInfo.fields
            .map((f) => f.xdm?.entityName)
            .filter((i) => !!i) as string[]
        )
    );

  const fieldsXdmDataFilters = useMemo(() => {
    if (xdmEntitiesDataLoading) {
      return {};
    }

    return getFieldsXdmDataFilters(
      xdmContextInfo,
      fieldsDependencies,
      currentValues,
      xdmEntitiesData ?? {}
    );
  }, [
    fieldsDependencies,
    currentValues,
    xdmEntitiesData,
    xdmContextInfo,
    xdmEntitiesDataLoading,
  ]);

  const isValid = useMemo(
    () => !(errors && Object.keys(errors).length),
    [errors]
  );

  const handleCancel = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    setShowErrors(true);
    isValid && onSubmit?.(currentValues);
  }, [currentValues, onSubmit, isValid]);

  const handleNewValue = useCallback(
    (
      fieldDependencies: FieldDependencies,
      newValue: Entity | Entity[] | null
    ) => {
      setCurrentValues((prevState: any) => {
        const newValues = {
          ...prevState,
          [fieldDependencies.field]: newValue,
        };
        onChange?.(newValues);
        return newValues;
      });
    },
    [onChange]
  );

  const xdmFieldsValidationSchema = useMemo(() => {
    const schema =
      field.xdmContext?.fields.reduce(
        (acc: { [key: string]: any }, item?: Field) => {
          if (!item?.required || item?.ignore) return acc;

          if (item.isArray) {
            acc[item.name] = yup
              .array()
              .of(yup.object())
              .required(requiredFieldMessage)
              .min(1, requiredFieldMessage);
          } else {
            acc[item.name] = yup.object().required(requiredFieldMessage);
          }

          return acc;
        },
        {}
      ) || {};
    return yup.object().shape(schema);
  }, [field.xdmContext?.fields]);

  useEffect(() => {
    let validationErrors: { [key: string]: string } = {};
    try {
      xdmFieldsValidationSchema.validateSync(currentValues, {
        abortEarly: false,
      });
    } catch (error) {
      validationErrors = (error as ValidationError).inner.reduce(
        (acc: { [key: string]: string }, validationError: ValidationError) => {
          if (validationError.type !== 'array.base') {
            acc[validationError.path as any] = validationError.message;
          }
          return acc;
        },
        {}
      );
    }
    setErrors(
      Object.keys(validationErrors).length > 0 ? validationErrors : null
    );
  }, [currentValues, xdmFieldsValidationSchema]);

  const renderSelectComponent = useCallback(
    (field: FieldDependencies) => {
      let component: ReactElement;

      if (field.fieldInfo.isArray) {
        component = (
          <XdmMultiselectDropdown
            fieldInfo={field.fieldInfo}
            value={currentValues[field.field] ?? []}
            onChange={(newValue) => {
              handleNewValue(field, newValue);
            }}
            filter={fieldsXdmDataFilters[field.field]}
            dataTestId={`pos-multiselect-${field.field}`}
          />
        );
      } else {
        component = (
          <XdmSelectDropdown
            fieldInfo={field.fieldInfo}
            value={currentValues[field.field]}
            onChange={(newValue) => {
              handleNewValue(field, newValue);
            }}
            filter={fieldsXdmDataFilters[field.field]}
          />
        );
      }

      return component;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValues, currentValues, fieldsXdmDataFilters, handleNewValue]
  );

  return (
    <div className="xdmcontext-item">
      {header && <div className="header">{header}</div>}

      {!xdmEntitiesDataLoading &&
        range(1, Math.ceil(fieldsDependencies.length / itemsPerRow)).map(
          (i) => (
            <div className="xdmcontext-row" key={i}>
              {fieldsDependencies
                .slice(
                  (i - 1) * itemsPerRow,
                  (i - 1) * itemsPerRow + itemsPerRow
                )
                .map((f) => {
                  return (
                    <div
                      className={clsx(
                        'xdmcontext-field',
                        showErrors &&
                          errors &&
                          errors[f.fieldInfo.name] &&
                          'error'
                      )}
                      data-testid="xdmcontext-field"
                      key={f.fieldInfo.name}
                    >
                      {renderSelectComponent(f)}
                      {showErrors && errors && errors[f.fieldInfo.name] && (
                        <Label basic prompt pointing="above" color="red">
                          {errors[f.fieldInfo.name]}
                        </Label>
                      )}
                    </div>
                  );
                })}
            </div>
          )
        )}
      {!isFilterItem && (
        <div className="actions">
          <POSButton floated="right" onClick={handleCancel}>
            Cancel
          </POSButton>
          <POSButton floated="right" onClick={handleSave}>
            Ok
          </POSButton>
        </div>
      )}
    </div>
  );
};
